import type { CSSProperties } from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Skeleton from "../../components/states/Skeleton";
import { listCustomers, type CustomerRow, type Tier } from "../../lib/admin/customers";
import { fetchEntityHealth } from "../../lib/admin/health";
import { fetchRecentImpersonations, type ImpersonationEvent } from "../../lib/admin/overview";
import {
  buildAlerts,
  summarizeCustomerHealth,
  type CustomerHealthSummary,
  type OverviewAlert,
} from "../../lib/admin/overviewSummary.mjs";

/**
 * MER-213 v1: the /admin landing page. Single morning check-in
 * surface: customer counts, plain-English alerts, per-customer health
 * cards (sync_log-derived, reusing the MER-477 connectorHealth
 * module), and the impersonation audit feed.
 *
 * Deliberately absent until MER-208 instrumentation exists: Recent
 * Logins and the no-recent-login alert (dashboard_event table is not
 * built; no empty-frame surfaces). See the MER-185 plan comment.
 */

const TIER_LABELS: Record<Tier, string> = {
  growth: "Growth",
  professional: "Pro",
  enterprise: "Enterprise",
};

const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;

function relativeTime(iso: string | null): string {
  if (!iso) return "Never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "Never";
  const delta = Date.now() - t;
  if (delta < 60 * 1000) return "just now";
  if (delta < ONE_HOUR_MS) return `${Math.max(1, Math.round(delta / (60 * 1000)))}m ago`;
  if (delta < ONE_DAY_MS) return `${Math.round(delta / ONE_HOUR_MS)}h ago`;
  return `${Math.round(delta / ONE_DAY_MS)}d ago`;
}

const FRESHNESS_TONE: Record<CustomerHealthSummary["freshness"], string> = {
  fresh: "var(--flag-green)",
  aging: "var(--flag-yellow)",
  stale: "var(--flag-red)",
  none: "var(--text-muted)",
};

interface CustomerHealth {
  customer: CustomerRow;
  summary: CustomerHealthSummary;
}

interface PageState {
  customers: CustomerHealth[];
  alerts: OverviewAlert[];
  impersonations: ImpersonationEvent[];
  isLoading: boolean;
  error: string | null;
}

export default function AdminOverview() {
  const [state, setState] = useState<PageState>({
    customers: [],
    alerts: [],
    impersonations: [],
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [customers, impersonations] = await Promise.all([
          listCustomers(),
          fetchRecentImpersonations(10),
        ]);
        const healths = await Promise.all(
          customers.map(async (customer) => ({
            customer,
            summary: summarizeCustomerHealth(await fetchEntityHealth(customer.tenantId)),
          })),
        );
        if (cancelled) return;
        const alerts = buildAlerts(
          healths.map((h) => ({
            slug: h.customer.slug,
            name: h.customer.name,
            summary: h.summary,
          })),
        );
        setState({ customers: healths, alerts, impersonations, isLoading: false, error: null });
      } catch (err) {
        if (cancelled) return;
        setState((s) => ({
          ...s,
          isLoading: false,
          error: err instanceof Error ? err.message : String(err),
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main style={{ padding: "24px 32px", display: "grid", gap: 18 }}>
      <header
        style={{
          padding: "22px 28px",
          background: "linear-gradient(110deg, var(--brand-mint) 0%, #E6F3EE 50%, #FFFFFF 100%)",
          border: "1px solid var(--brand-line)",
          borderRadius: 8,
        }}
      >
        <div
          style={{
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: 1,
            color: "var(--text-2)",
            fontWeight: 700,
          }}
        >
          Admin
        </div>
        <h1
          style={{
            margin: "6px 0 0 0",
            fontSize: 26,
            fontWeight: 800,
            letterSpacing: -0.5,
            color: "var(--text)",
          }}
        >
          Overview
        </h1>
      </header>

      {state.isLoading ? <Skeleton label="Loading overview" height={220} /> : null}
      {state.error ? <ErrorCard message={state.error} /> : null}
      {!state.isLoading && !state.error ? (
        <>
          <CountStrip customers={state.customers.map((c) => c.customer)} />
          <AlertsPanel alerts={state.alerts} />
          <CustomerCards customers={state.customers} />
          <ImpersonationsPanel
            events={state.impersonations}
            customers={state.customers.map((c) => c.customer)}
          />
        </>
      ) : null}
    </main>
  );
}

function CountStrip(props: { customers: CustomerRow[] }) {
  const { customers } = props;
  const byTier = (tier: Tier) => customers.filter((c) => c.tier === tier).length;
  const cards: Array<{ label: string; value: number }> = [
    { label: "Customers", value: customers.length },
    { label: TIER_LABELS.growth, value: byTier("growth") },
    { label: TIER_LABELS.professional, value: byTier("professional") },
    { label: TIER_LABELS.enterprise, value: byTier("enterprise") },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
      {cards.map((card) => (
        <div key={card.label} style={{ ...panelStyle, gap: 4 }}>
          <div style={metricLabelStyle}>{card.label}</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: "var(--text)" }}>{card.value}</div>
        </div>
      ))}
    </div>
  );
}

function AlertsPanel(props: { alerts: OverviewAlert[] }) {
  const { alerts } = props;
  return (
    <section style={panelStyle}>
      <h2 style={panelTitleStyle}>Alerts</h2>
      {alerts.length === 0 ? (
        <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--flag-green)" }}>
          All systems nominal.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {alerts.map((alert, i) => (
            <Link
              key={`${alert.slug}-${i}`}
              to={`/admin/customers/${alert.slug}`}
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                gap: 10,
                alignItems: "center",
                padding: "10px 12px",
                border: "1px solid var(--border-subtle)",
                borderRadius: 8,
                background:
                  alert.severity === "red" ? "rgba(192,57,43,0.06)" : "rgba(255,193,7,0.08)",
                textDecoration: "none",
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  padding: "2px 8px",
                  borderRadius: 8,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  background:
                    alert.severity === "red" ? "rgba(192,57,43,0.12)" : "rgba(255,193,7,0.16)",
                  color: alert.severity === "red" ? "var(--flag-red)" : "var(--flag-yellow)",
                }}
              >
                {alert.severity === "red" ? "Red" : "Amber"}
              </span>
              <span style={{ fontSize: 13, color: "var(--text)" }}>{alert.message}</span>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function CustomerCards(props: { customers: CustomerHealth[] }) {
  const { customers } = props;
  return (
    <section style={panelStyle}>
      <h2 style={panelTitleStyle}>Customer health</h2>
      {customers.length === 0 ? (
        <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-muted)" }}>
          No customers onboarded yet.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
          {customers.map(({ customer, summary }) => (
            <Link
              key={customer.tenantId}
              to={`/admin/customers/${customer.slug}`}
              style={{
                display: "grid",
                gap: 8,
                padding: "14px 16px",
                border: "1px solid var(--border-subtle)",
                borderRadius: 8,
                background: "var(--bg-card)",
                textDecoration: "none",
              }}
            >
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>
                  {customer.name}
                </span>
                {customer.tier ? (
                  <span style={tierPillStyle}>{TIER_LABELS[customer.tier]}</span>
                ) : null}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                <CardStat
                  label="Last sync"
                  value={relativeTime(summary.lastSyncAt)}
                  tone={FRESHNESS_TONE[summary.freshness]}
                />
                <CardStat
                  label="Failing entities"
                  value={String(summary.failedCount)}
                  tone={summary.failedCount > 0 ? "var(--flag-red)" : "var(--flag-green)"}
                />
                <CardStat
                  label="Connectors down"
                  value={String(summary.downConnectors.length)}
                  tone={
                    summary.downConnectors.length > 0 ? "var(--flag-yellow)" : "var(--flag-green)"
                  }
                />
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function CardStat(props: { label: string; value: string; tone: string }) {
  return (
    <div>
      <div style={metricLabelStyle}>{props.label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: props.tone }}>{props.value}</div>
    </div>
  );
}

function ImpersonationsPanel(props: { events: ImpersonationEvent[]; customers: CustomerRow[] }) {
  const { events, customers } = props;
  const nameByTenant = new Map(customers.map((c) => [c.tenantId, c.name]));
  return (
    <section style={panelStyle}>
      <h2 style={panelTitleStyle}>Recent impersonations</h2>
      {events.length === 0 ? (
        <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-muted)" }}>
          No impersonations recorded yet.
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={headerCellStyle}>When</th>
              <th style={headerCellStyle}>Customer</th>
              <th style={headerCellStyle}>Event</th>
              <th style={headerCellStyle}>Staff</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id}>
                <td style={cellStyle}>{relativeTime(e.occurredAt)}</td>
                <td style={cellStyle}>{nameByTenant.get(e.targetTenantId) ?? e.targetTenantId}</td>
                <td style={{ ...cellStyle, textTransform: "capitalize" }}>{e.eventType}</td>
                {/* uuid prefix until an email view exists; see lib/admin/overview.ts */}
                <td style={{ ...cellStyle, color: "var(--text-muted)" }}>
                  {e.staffUserId.slice(0, 8)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function ErrorCard(props: { message: string }) {
  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: 12,
        padding: 20,
        color: "var(--flag-red)",
        fontSize: 13,
      }}
    >
      {props.message}
    </div>
  );
}

const panelStyle: CSSProperties = {
  background: "var(--bg-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: 12,
  padding: 18,
  display: "grid",
  gap: 12,
};

const panelTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 14,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  color: "var(--text-2)",
};

const metricLabelStyle: CSSProperties = {
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--text-muted)",
  fontWeight: 700,
};

const tierPillStyle: CSSProperties = {
  display: "inline-block",
  fontSize: 10,
  padding: "3px 9px",
  borderRadius: 12,
  background: "rgba(15,107,92,0.10)",
  color: "var(--brand-deep)",
  border: "1px solid var(--brand-line)",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  fontWeight: 700,
};

const cellStyle: CSSProperties = {
  padding: "10px 14px",
  fontSize: 13,
  borderBottom: "1px solid var(--border-subtle)",
  verticalAlign: "top",
  color: "var(--text)",
};

const headerCellStyle: CSSProperties = {
  textAlign: "left",
  padding: "8px 14px",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--text-muted)",
  borderBottom: "1px solid var(--border-subtle)",
  fontWeight: 700,
};
